/** Pure marketplace maths and display rules for the points model.
 *
 * Deliberately a non-component module: these live outside `MarketplaceScreen.tsx` so that file
 * exports only its component and Vite's Fast Refresh can preserve state across edits
 * (`react-doctor/only-export-components`). It also keeps the rules unit-testable without
 * rendering anything.
 *
 * The normative source is `0 / Points Algo` (Linear): 1 CHF = 10 pts earned, and a reward costs
 * `ceil50(price CHF × 100)` — so 100 pts = 1 CHF of menu value, a ~10% give-back.
 */
import type { MarketplaceItem, MenuItem } from '../api/types'
import type { PosAccountLine } from '../lightspeed/types'

/** Swiss point formatting: `1250` → `1'250` (Figma 2264:2151).
 *
 * Deliberately NOT `Intl.NumberFormat('de-CH')` — CLDR renders the group separator as a curly
 * `’` (U+2019) and the exact glyph varies with the ICU build shipped in the POS webview. The
 * design specifies a straight apostrophe and the cashier compares this number against the POS,
 * so pin it rather than inherit whatever the device decides. */
export function formatPoints(value: number): string {
  return String(Math.trunc(Math.abs(value)))
    .replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    .replace(/^/, value < 0 ? '-' : '')
}

/** Menu price in CHF → points cost: `ceil50(price × 100)`, minimum 50 (Points Algo §3).
 *
 * `Math.round` to whole cents BEFORE the ceil: `6.51 * 100` is `651.0000000000001` in binary
 * floating point, and feeding that straight into a ceiling can kick an exact price up a whole
 * 50-point step. Points are money here, so the drift is snapped out rather than hoped away —
 * Points Algo invariant 1 ("rounding is never more generous than intended") only holds if the
 * input is exact in the first place. */
export function pointsForPrice(priceChf: number): number {
  const cents = Math.round(priceChf * 100)
  return Math.max(50, Math.ceil(cents / 50) * 50)
}

/** Order for the grid: claimable first (cheapest first), then the rest (cheapest first).
 *
 * The section is titled "réclamable maintenant", so what the customer can actually have has to
 * lead — the cashier reads the first cards out loud and never scrolls during a rush. */
export function orderForDisplay(items: MarketplaceItem[], balance: number): MarketplaceItem[] {
  const claimable = (i: MarketplaceItem) => !i.locked && i.points_cost <= balance
  return [...items].sort((a, b) => {
    if (claimable(a) !== claimable(b)) return claimable(a) ? -1 : 1
    return a.points_cost - b.points_cost
  })
}

/** The customer's spendable state during one till session. */
export interface Wallet {
  balance: number
  claimed: ReadonlySet<string>
}

/** Debit a reward from the wallet, idempotently.
 *
 * Returns the SAME object when the reward was already claimed, so a double-fire (double tap, a
 * stray re-dispatch, a re-render racing the disabled attribute) can never debit the customer
 * twice for one reward. Claiming is the only thing on this screen that spends points, so the
 * guard lives here rather than relying on the button being disabled. */
export function applyClaim(wallet: Wallet, item: MarketplaceItem): Wallet {
  if (wallet.claimed.has(item.reward_id)) return wallet
  return {
    balance: wallet.balance - item.points_cost,
    claimed: new Set(wallet.claimed).add(item.reward_id),
  }
}

/** Undo a claim: give the points back and forget it was claimed.
 *
 * Returns the SAME object when the reward was not claimed, mirroring `applyClaim` — so a stray
 * second revoke cannot credit the customer points they never spent. Claim and revoke are exact
 * inverses, which is what makes a mis-tap at the till recoverable. */
export function revokeClaim(wallet: Wallet, item: MarketplaceItem): Wallet {
  if (!wallet.claimed.has(item.reward_id)) return wallet
  const claimed = new Set(wallet.claimed)
  claimed.delete(item.reward_id)
  return { balance: wallet.balance + item.points_cost, claimed }
}

export type KeypadMode = 'chf' | 'points'

/** Points earned for a manually-entered amount (Points Algo §1: `points = ticket CHF × 10`).
 *
 * Truncates rather than rounds — 13.55 CHF is 135 pts, not 136. Points Algo invariant 1 says
 * rounding is never more generous than intended, and the keypad's own caption promises the
 * amount is taken to the 10-centime step. Parsed via cents so `13.55 * 10` can't drift.
 *
 * In `points` mode the entry IS the point count, so it passes through untouched (a merchant
 * granting a goodwill balance shouldn't have it silently multiplied).
 */
export function pointsForAmount(entry: string, mode: KeypadMode): number {
  const normalized = entry.replace(',', '.')
  const value = Number.parseFloat(normalized)
  if (!Number.isFinite(value) || value <= 0) return 0
  if (mode === 'points') return Math.floor(value)
  return Math.floor(Math.round(value * 100) / 10)
}

/** Apply one keypad press to the current entry (Figma 2260:383).
 *
 * Constrains what can be typed so the cashier can't build an amount the POS could never produce:
 * one separator only, at most two decimals, no leading zeros, and no separator at all in points
 * mode (fractional points don't exist). */
export function pressKey(entry: string, key: string, mode: KeypadMode): string {
  if (key === '⌫') return entry.slice(0, -1)
  if (key === ',') {
    if (mode === 'points' || entry.includes(',')) return entry
    return entry === '' ? '0,' : `${entry},`
  }
  const [whole, decimals] = entry.split(',')
  if (decimals !== undefined && decimals.length >= 2) return entry
  // Bare "0" is a placeholder, not a real digit — typing 5 after it means 5, not 05.
  if (entry === '0') return key
  if (whole !== undefined && decimals === undefined && whole.length >= 6) return entry
  return entry + key
}

/** Filter the merchant's menu for the link dialog's type-ahead. Accent- and case-insensitive,
 * because the cashier types "poke" for "Poké" under time pressure. */
export function matchMenu(items: MenuItem[], query: string): MenuItem[] {
  const fold = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  const q = fold(query.trim())
  if (!q) return items
  return items.filter((i) => {
    // Substring match on text, NOT a set-membership test — a Set can't answer "contains", so the
    // O(n·m) here is inherent to the search, not a lookup that wants indexing.
    const text = fold(i.name ?? '')
    return text.includes(q)
  })
}

/** The lines on the open order that this reward's linked menu item could be applied to.
 *
 * Matching is on the DURABLE menu item id, which is why the link is stored against the menu and
 * not against a line: the same reward has to resolve on every future ticket. An order can hold
 * several separate lines of the same item (rung up individually, split across courses, different
 * seats), and each is a distinct discount target — so the caller must ask which one rather than
 * silently discounting the first, which could be the wrong seat's or the wrong price.
 *
 * Matched on `itemSku`, NOT `itemId`: a device dump (2026-07-29) showed the raw line carries both
 * `itemSku` ("13") and an internal `itemIdentifier` ("1399154316148985"), and `pos_item_id` holds
 * the SKU. Matching on `itemId` compares those two and silently never fires. */
export function matchingOrderLines(
  lines: PosAccountLine[],
  posItemId: string | undefined,
): PosAccountLine[] {
  if (!posItemId) return []
  return lines.filter((l) => l.itemSku === posItemId)
}

/** Rebuild the wallet from the rewards already tagged on the open sale.
 *
 * The cashier can close the extension mid-sale and reopen it — the POS keeps the ticket, so we
 * have to come back to the same state or the customer gets charged twice or claims twice. The
 * balance is derived (start minus what was claimed) rather than stored: the sale's external
 * references are the only durable record we have, and a derived number cannot drift from them.
 *
 * Unknown ids are ignored, not counted — a reward tagged by an older build (or since removed from
 * the catalogue) must not silently subtract points nobody can account for. */
export function restoreWallet(
  items: MarketplaceItem[],
  startingBalance: number,
  claimedIds: string[],
): Wallet {
  const byId = new Map(items.map((i) => [i.reward_id, i]))
  const claimed = new Set<string>()
  let spent = 0
  for (const id of claimedIds) {
    const item = byId.get(id)
    if (!item || claimed.has(id)) continue
    claimed.add(id)
    spent += item.points_cost
  }
  return { balance: startingBalance - spent, claimed }
}

/** Did the discount actually land on this line?
 *
 * `pos_toggleDiscount` resolving only means the HOST ACCEPTED THE CALL, not that anything was
 * discounted — a device dump (2026-07-29) came back with `discounts: []` on every line and an
 * empty `pos_getAvailableDiscounts`, which is what a merchant with no BADGER_FREE configured looks
 * like. Treating "the call returned" as "the customer got their free coffee" is how you give away
 * a reward that was never taken off the bill, so a redemption is confirmed by RE-READING the
 * account and asking this. Invariant 2: a discount is money.
 *
 * A missing line (id no longer on the ticket) is not applied — deliberately, not defensively:
 * it means the line was voided under us. */
export function lineHasDiscount(line: PosAccountLine | undefined): boolean {
  return !!line && Array.isArray(line.discounts) && line.discounts.length > 0
}

/** Font size for a reward card's title, stepped down as the name gets longer.
 *
 * The card is a fixed 357px wide and the points pill eats the right-hand third, so a long name
 * like "Lasagne Calabrese" runs out of room at the design's 20px and gets truncated — on a
 * marketplace where the name IS the product, an ellipsis is the worst thing to lose.
 *
 * Stepping the size by character count rather than measuring: it is deterministic, needs no
 * layout pass, and cannot cause the reflow-loop that measure-then-resize does mid-render. The
 * thresholds are where 357px runs out at each size.
 */
export function titleSizeClass(label: string): string {
  const n = label.trim().length
  if (n <= 13) return 'text-[20px]'
  if (n <= 18) return 'text-[17px]'
  return 'text-[15px]'
}

export type CardStatus = 'claimable' | 'claimed' | 'locked' | 'unaffordable'

/** Which of the four card states an item is in, in priority order.
 *
 * `claimed` MUST outrank `unaffordable`: claiming deducts the cost, so the reward the cashier just
 * granted is normally no longer affordable — checking affordability first labels a successful
 * claim "Points insuffisants", which at the till reads as a failure. `locked` (price drift,
 * TechSpec B5) outranks affordability too: it is a merchant-action problem, not a balance one. */
export function cardStatus(item: MarketplaceItem, balance: number, claimed: boolean): CardStatus {
  if (claimed) return 'claimed'
  if (item.locked) return 'locked'
  if (item.points_cost > balance) return 'unaffordable'
  return 'claimable'
}
